import { PM_RULE_KEY_PREFIX, WorkfrontLookup, WorkfrontMappingKind } from '@atlas/types'
import { CardShell } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useActivePms } from '@/hooks/useUsers'
import { useLookupRows, useMappingStatus } from '@/hooks/useWorkfrontMappings'
import type { LookupListRow, MappingDraftDTO } from '@/services/workfront-mappings.service'
import { PmRulesTable } from './mappings/components/PmRulesTable'
import { DraftControls } from './mappings/DraftControls'
import { useActorName, useDrawerContext, useReportLoadFailure } from './mappings/tab-support'
import { NEW_ROW_KEY, useMappingEditor } from './mappings/useMappingEditor'
import { WorkfrontHeader } from './shared'

/** The draft's changes that are not PM rules: publishing from here publishes them too. */
export const otherChangesOf = (draft: MappingDraftDTO | null): number =>
	draft?.changes.filter((change) => !change.key.startsWith(PM_RULE_KEY_PREFIX)).length ?? 0

/**
 * Admin › Workfront › PM rules: which PM a work order from Workfront gets, by its
 * geography. The rules are the `region_to_pm` lookup, so they go through the mapping
 * draft: saved here, previewed and published with the draft bar.
 */
export function WorkfrontPmRulesPage() {
	const status = useMappingStatus()
	const query = useLookupRows(WorkfrontLookup.REGION_TO_PM)
	const rows = query.data ?? []
	const editor = useMappingEditor(WorkfrontMappingKind.LOOKUP, (key) => rows.find((r) => r.key === key)?.row)
	const context = useDrawerContext()
	const nameOf = useActorName()
	const pms = useActivePms()
	// Until the PMs load, no rule is flagged.
	const isActivePmId = (userId: string) => !pms.data || pms.data.some((user) => user.userId === userId)
	useReportLoadFailure(query.isError, 'PM rules')
	const draft = status.data?.draft ?? null
	const others = otherChangesOf(draft)

	const edit = (rule: LookupListRow) =>
		editor.open({
			key: rule.key,
			row: rule.row,
			title: `Edit the rule for ${rule.sourceLabel ?? rule.row.sourceValue}`,
			canDelete: rule.status !== 'deleted',
		})
	const add = () =>
		editor.open({
			key: NEW_ROW_KEY,
			row: { lookup: WorkfrontLookup.REGION_TO_PM, sourceValue: '', targetValue: '' },
			title: 'Add a PM rule',
		})

	return (
		<div className="space-y-6">
			<WorkfrontHeader title="PM rules" actions={<Button onPress={add}>Add rule</Button>} />
			<p className="max-w-3xl text-ds-sm text-ds-text-muted">
				A brief from Workfront gets the PM of the rule for its country, else for the nearest region above it, else the
				default. A PM someone chose is never replaced. Rules take effect once published.
			</p>
			<DraftControls draft={draft} />
			{draft && others > 0 ? (
				<p className="text-ds-sm">
					{others === 1
						? '1 other change in this draft will be published with the PM rules.'
						: `${others.toLocaleString('en-US')} other changes in this draft will be published with the PM rules.`}
				</p>
			) : null}
			<CardShell>
				<PmRulesTable
					rows={rows}
					nameOf={nameOf}
					isActivePm={isActivePmId}
					isLoading={query.isLoading}
					isError={query.isError}
					onRetry={() => void query.refetch()}
					onEdit={edit}
				/>
			</CardShell>
			{editor.drawer(context)}
		</div>
	)
}
